import styled, { css } from 'styled-components'
import { cardisTheme } from '../../theme/cardisTheme'

// Re-export shared styles from CardisPropertyCharacteristics
export {
  PageContainer,
  ContentArea,
  Title,
  SectionHeader,
  FooterWrapper,
  FooterPattern,
  Footer,
  PageNumber,
} from '../CardisPropertyCharacteristics/CardisPropertyCharacteristics.styles'

const printStyles = css`
  @media print {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// Section wrapper for spacing
export const SectionWrapper = styled.div`
  margin-bottom: 24px;

  @media print {
    margin-bottom: 24px !important;
  }
`

// VALEUR TECHNIQUE - Full width 4-column table
export const TechniqueTable = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  @media print {
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
  }
`

export const TechniqueRow = styled.div<{ $isTotal?: boolean }>`
  display: grid;
  grid-template-columns: 200px 1fr 50px 120px;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid
    ${({ $isTotal }) =>
      $isTotal ? cardisTheme.colors.secondary : cardisTheme.colors.borderLight};
  align-items: baseline;

  ${({ $isTotal }) =>
    $isTotal &&
    css`
      padding-top: 8px;
      border-top: 2px solid ${cardisTheme.colors.secondary};
      border-bottom: none;
    `}
`

export const TechniqueLabel = styled.span<{ $isTotal?: boolean }>`
  font-family: ${cardisTheme.fonts.body};
  font-size: ${({ $isTotal }) => ($isTotal ? '16px' : '14px')};
  font-weight: ${({ $isTotal }) => ($isTotal ? 500 : 400)};
  color: ${({ $isTotal }) =>
    $isTotal ? cardisTheme.colors.secondary : cardisTheme.colors.primary};

  @media print {
    ${printStyles}
  }
`

export const TechniqueDetails = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 14px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};
  text-align: right;

  @media print {
    ${printStyles}
  }
`

export const TechniqueCurrency = styled.span<{ $isTotal?: boolean }>`
  font-family: ${cardisTheme.fonts.body};
  font-size: ${({ $isTotal }) => ($isTotal ? '16px' : '14px')};
  font-weight: 400;
  color: ${({ $isTotal }) =>
    $isTotal ? cardisTheme.colors.secondary : cardisTheme.colors.textLight};
  text-align: right;

  @media print {
    ${printStyles}
  }
`

export const TechniqueValue = styled.span<{
  $isTotal?: boolean
  $isNegative?: boolean
}>`
  font-family: ${cardisTheme.fonts.body};
  font-size: ${({ $isTotal }) => ($isTotal ? '16px' : '14px')};
  font-weight: ${({ $isTotal }) => ($isTotal ? 600 : 400)};
  color: ${({ $isTotal, $isNegative }) =>
    $isTotal
      ? cardisTheme.colors.secondary
      : $isNegative
        ? cardisTheme.colors.textLight
        : cardisTheme.colors.textLight};
  text-align: right;

  @media print {
    ${printStyles}
  }
`

// ESTIMATION HÉDONISTE - Horizontal layout
export const HedonicSection = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr;
  gap: 32px;
  align-items: flex-start;
  margin-top: 8px;

  @media print {
    display: grid !important;
    grid-template-columns: 3fr 1fr 1fr !important;
    gap: 32px !important;
    margin-top: 8px !important;
  }
`

export const HedonicMainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
  }
`

export const HedonicMainValue = styled.div`
  font-family: ${cardisTheme.fonts.body};
  font-size: 24px;
  font-weight: 600;
  color: ${cardisTheme.colors.secondary};
  line-height: 1.1;

  @media print {
    ${printStyles}
  }
`

export const HedonicPricePerSqm = styled.div`
  font-family: ${cardisTheme.fonts.body};
  font-size: 14px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};

  @media print {
    font-size: 14px !important;
    color: ${cardisTheme.colors.textLight} !important;
    ${printStyles}
  }
`

export const HedonicRangeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
  }
`

export const HedonicRangeLabel = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};

  @media print {
    font-size: 12px !important;
    color: ${cardisTheme.colors.textLight} !important;
    ${printStyles}
  }
`

export const HedonicRangeValue = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 14px;
  font-weight: 600;
  color: ${cardisTheme.colors.secondary};

  @media print {
    ${printStyles}
  }
`

export const HedonicRangePricePerSqm = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};

  @media print {
    font-size: 12px !important;
    color: ${cardisTheme.colors.textLight} !important;
    ${printStyles}
  }
`

// Chart section
export const ChartSection = styled.div`
  margin-top: -20px;
`

// Property images section
export const ImagesSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 12px;
  padding: 0 24px;

  @media print {
    display: grid !important;
    gap: 16px !important;
    margin-top: 12px !important;
  }
`

export const PropertyImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 0;

  @media print {
    ${printStyles}
  }
`

export const ImagePlaceholder = styled.div`
  width: 100%;
  height: 140px;
  background: ${cardisTheme.colors.backgroundGray};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${cardisTheme.colors.textLight};
  font-size: 12px;

  @media print {
    width: 100% !important;
    height: 140px !important;
    background: ${cardisTheme.colors.backgroundGray} !important;
    ${printStyles}
  }
`
